import { useState, useEffect, useCallback } from 'react';
import { campaignApi, type Campaign } from '../../api/campaignApi';
import { clientApi } from '../../api/clientApi';
import { clientGroupApi, type ClientGroup } from '../../api/clientGroupApi';
import { useSalonId } from '../../hooks/useSalonId';

// ============================================
// ICONS (inline SVG to avoid dependency)
// ============================================

const MegaphoneIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-5 h-5">
        <path strokeLinecap="round" strokeLinejoin="round" d="M10.34 15.84c-.688-.06-1.386-.09-2.09-.09H7.5a4.5 4.5 0 110-9h.75c.704 0 1.402-.03 2.09-.09m0 9.18c.253.962.584 1.892.985 2.783.247.55.06 1.21-.463 1.511l-.657.38c-.551.318-1.26.117-1.527-.461a20.845 20.845 0 01-1.44-4.282m3.102.069a18.03 18.03 0 01-.59-4.59c0-1.586.205-3.124.59-4.59m0 9.18a23.848 23.848 0 018.835 2.535M10.34 6.66a23.847 23.847 0 008.835-2.535m0 0A23.74 23.74 0 0018.795 3m.38 1.125a23.91 23.91 0 011.014 5.395m-1.014 8.855c-.118.38-.245.754-.38 1.125m.38-1.125a23.91 23.91 0 001.014-5.395m0-3.46c.495.413.811 1.035.811 1.73 0 .695-.316 1.317-.811 1.73m0-3.46a24.347 24.347 0 010 3.46" />
    </svg>
);

const PlusIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2.5} stroke="currentColor" className="w-4 h-4">
        <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
    </svg>
);

const XIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-5 h-5">
        <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
    </svg>
);

const UsersIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4">
        <path strokeLinecap="round" strokeLinejoin="round" d="M15 19.128a9.38 9.38 0 002.625.372 9.337 9.337 0 004.121-.952 4.125 4.125 0 00-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 018.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0111.964-3.07M12 6.375a3.375 3.375 0 11-6.75 0 3.375 3.375 0 016.75 0zm8.25 2.25a2.625 2.625 0 11-5.25 0 2.625 2.625 0 015.25 0z" />
    </svg>
);

// ============================================
// Status Badge
// ============================================

function StatusBadge({ status, sentCount, totalRecipients }: { status: Campaign['status'], sentCount: number, totalRecipients: number }) {
    const configs = {
        COMPLETED: { label: '✅ Completada', classes: 'bg-emerald-100 text-emerald-700' },
        IN_PROGRESS: { label: '🔄 En Progreso', classes: 'bg-blue-100 text-blue-700 animate-pulse' },
        SCHEDULED: { label: '📅 Programada', classes: 'bg-amber-100 text-amber-700' },
        DRAFT: { label: '📝 Borrador', classes: 'bg-gray-100 text-gray-600' },
        FAILED: { label: '❌ Fallida', classes: 'bg-red-100 text-red-700' },
        CANCELED: { label: '⏹ Cancelada', classes: 'bg-gray-100 text-gray-500' },
    } as const;

    const { label, classes } = configs[status] || configs.DRAFT;

    return (
        <div className="flex flex-col gap-1">
            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold ${classes}`}>
                {label}
            </span>
            {status === 'IN_PROGRESS' && totalRecipients > 0 && (
                <div className="w-24 h-1.5 bg-gray-200 rounded-full overflow-hidden">
                    <div
                        className="h-full bg-gradient-to-r from-blue-500 to-teal-400 rounded-full transition-all duration-1000"
                        style={{ width: `${Math.round((sentCount / totalRecipients) * 100)}%` }}
                    />
                </div>
            )}
        </div>
    );
}

// ============================================
// Create Campaign Modal (Multi-Step)
// ============================================

interface CreateDrawerProps {
    isOpen: boolean;
    onClose: () => void;
    onCreated: () => void;
    salonId: string;
    clientCount: number;
    groups: ClientGroup[];
}

function CreateCampaignDrawer({ isOpen, onClose, onCreated, salonId, clientCount, groups }: CreateDrawerProps) {
    const [name, setName] = useState('');
    const [selectedGroupId, setSelectedGroupId] = useState<string | null>(null);
    const [messageTemplate, setMessageTemplate] = useState('');
    const [scheduledAt, setScheduledAt] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [step, setStep] = useState(1);
    const MAX_CHARS = 1024;

    const resetForm = () => { setName(''); setSelectedGroupId(null); setMessageTemplate(''); setScheduledAt(''); setError(null); setStep(1); };
    const handleClose = () => { resetForm(); onClose(); };

    const handleCreate = async (scheduleNow: boolean) => {
        if (!name.trim() || !messageTemplate.trim()) { setError('El nombre y el mensaje son obligatorios.'); return; }
        setError(null); setLoading(true);
        try {
            const campaign = await campaignApi.createCampaign(salonId, {
                name: name.trim(), messageTemplate: messageTemplate.trim(), scheduledAt: scheduledAt || null, groupId: selectedGroupId
            });
            if (scheduleNow) await campaignApi.scheduleCampaign(salonId, campaign.id);
            resetForm(); onCreated(); onClose();
        } catch (e: unknown) {
            const err = e as { response?: { data?: { message?: string } } };
            setError(err?.response?.data?.message || 'Error al crear la campaña.');
        } finally { setLoading(false); }
    };

    const previewMessage = messageTemplate.replace(/\{nombre\}/g, 'María García');
    const quickEmojis = ['✨', '🎉', '💐', '🔥', '⭐', '💎', '🎁', '❤️'];

    if (!isOpen) return null;

    const steps = [{ n: 1, label: 'Campaña' }, { n: 2, label: 'Mensaje' }, { n: 3, label: 'Envío' }];

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={handleClose} style={{ animation: 'fadeIn 0.2s ease-out' }} />

            <div className="relative w-full max-w-lg bg-white rounded-3xl shadow-2xl overflow-hidden flex flex-col" style={{ animation: 'scaleIn 0.25s cubic-bezier(0.34,1.56,0.64,1)', maxHeight: '90vh' }}>

                {/* Gradient Header */}
                <div className="bg-gradient-to-br from-violet-600 via-purple-600 to-fuchsia-600 px-6 pt-6 pb-8 relative overflow-hidden flex-shrink-0">
                    <div className="absolute -top-6 -right-6 w-32 h-32 rounded-full bg-white/10" />
                    <div className="absolute -bottom-4 -left-4 w-20 h-20 rounded-full bg-white/10" />
                    <div className="relative flex items-start justify-between mb-5">
                        <div>
                            <div className="flex items-center gap-2 mb-1">
                                <span className="text-2xl">📢</span>
                                <h2 className="text-xl font-bold text-white">Nueva Campaña</h2>
                            </div>
                            <p className="text-purple-200 text-sm">WhatsApp masivo · {selectedGroupId ? groups.find(g => g.id === selectedGroupId)?.memberCount : clientCount} contactos</p>
                        </div>
                        <button onClick={handleClose} className="p-2 text-white/70 hover:text-white hover:bg-white/20 rounded-xl transition-all"><XIcon /></button>
                    </div>

                    {/* Step Indicator */}
                    <div className="relative flex items-center">
                        {steps.map((s, i) => (
                            <div key={s.n} className="flex items-center flex-1 last:flex-none">
                                <button onClick={() => s.n < step && setStep(s.n)} className="flex flex-col items-center gap-1">
                                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold transition-all duration-300 ${step === s.n ? 'bg-white text-purple-700 shadow-lg scale-110' : step > s.n ? 'bg-white/30 text-white' : 'bg-white/15 text-white/50'}`}>
                                        {step > s.n ? '✓' : s.n}
                                    </div>
                                    <span className={`text-xs font-medium transition-colors ${step >= s.n ? 'text-white' : 'text-white/40'}`}>{s.label}</span>
                                </button>
                                {i < steps.length - 1 && (
                                    <div className="flex-1 h-0.5 mx-2 mb-5 rounded-full bg-white/20 overflow-hidden">
                                        <div className="h-full bg-white/70 rounded-full transition-all duration-500" style={{ width: step > s.n ? '100%' : '0%' }} />
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                </div>

                {/* Scrollable Content */}
                <div className="overflow-y-auto flex-1 p-6 space-y-5">

                    {/* STEP 1 */}
                    {step === 1 && (
                        <div style={{ animation: 'fadeSlideIn 0.3s ease-out' }}>
                            <div className="flex items-start gap-3 bg-amber-50 border border-amber-100 rounded-2xl p-4 mb-5">
                                <span className="text-lg flex-shrink-0">🛡️</span>
                                <p className="text-sm text-amber-800 leading-relaxed">Los mensajes se envían con <strong>intervalos de 6 segundos</strong> entre cada uno para proteger tu número de WhatsApp.</p>
                            </div>

                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-2">Nombre de la Campaña *</label>
                                <input type="text" value={name} onChange={e => setName(e.target.value)} placeholder="ej. Día de la Madre 💐"
                                    className="w-full px-4 py-3 bg-gray-50 rounded-xl text-sm border-2 border-transparent focus:border-purple-400 focus:bg-white outline-none transition-all font-medium"
                                    maxLength={100} autoFocus />
                                <p className="text-xs text-gray-400 mt-1">{name.length}/100</p>
                            </div>

                             <div className="space-y-3">
                                <label className="block text-sm font-semibold text-gray-700 mb-2">Seleccionar Destinatarios</label>
                                
                                {/* Group Selector */}
                                <div className="grid grid-cols-1 gap-3">
                                    <button 
                                        onClick={() => setSelectedGroupId(null)}
                                        className={`flex items-center justify-between p-4 rounded-2xl border-2 transition-all ${!selectedGroupId ? 'bg-emerald-50 border-emerald-400 shadow-sm' : 'bg-gray-50 border-transparent hover:border-gray-200'}`}
                                    >
                                        <div className="flex items-center gap-3 text-left">
                                            <div className={`w-10 h-10 rounded-full flex items-center justify-center ${!selectedGroupId ? 'bg-emerald-100 text-emerald-600' : 'bg-gray-200 text-gray-500'}`}><UsersIcon /></div>
                                            <div>
                                                <p className={`text-sm font-bold ${!selectedGroupId ? 'text-emerald-900' : 'text-gray-700'}`}>Todos los clientes</p>
                                                <p className={`text-xs ${!selectedGroupId ? 'text-emerald-600' : 'text-gray-500'}`}>Toda tu base de datos activa</p>
                                            </div>
                                        </div>
                                        <div className="text-right">
                                            <p className={`text-xl font-bold ${!selectedGroupId ? 'text-emerald-700' : 'text-gray-400'}`}>{clientCount}</p>
                                        </div>
                                    </button>

                                    {groups.map(group => (
                                        <button 
                                            key={group.id}
                                            onClick={() => setSelectedGroupId(group.id)}
                                            className={`flex items-center justify-between p-4 rounded-2xl border-2 transition-all ${selectedGroupId === group.id ? 'bg-emerald-50 border-emerald-400 shadow-sm' : 'bg-gray-50 border-transparent hover:border-gray-200'}`}
                                        >
                                            <div className="flex items-center gap-3 text-left">
                                                <div className={`w-10 h-10 rounded-full flex items-center justify-center ${selectedGroupId === group.id ? 'bg-emerald-100 text-emerald-600' : 'bg-gray-200 text-gray-500'}`}><UsersIcon /></div>
                                                <div>
                                                    <p className={`text-sm font-bold ${selectedGroupId === group.id ? 'text-emerald-900' : 'text-gray-700'}`}>{group.name}</p>
                                                    <p className={`text-xs ${selectedGroupId === group.id ? 'text-emerald-600' : 'text-gray-500'}`}>{group.description || 'Segmento personalizado'}</p>
                                                </div>
                                            </div>
                                            <div className="text-right">
                                                <p className={`text-xl font-bold ${selectedGroupId === group.id ? 'text-emerald-700' : 'text-gray-400'}`}>{group.memberCount}</p>
                                            </div>
                                        </button>
                                    ))}
                                </div>
                            </div>

                            {/* Estimated Time logic updated */}
                            {(() => {
                                const currentCount = selectedGroupId ? groups.find(g => g.id === selectedGroupId)?.memberCount || 0 : clientCount;
                                if (currentCount === 0) return null;
                                return (
                                    <div className="mt-4 bg-gray-50 rounded-xl p-3 flex items-center gap-3">
                                        <span className="text-lg">⏱️</span>
                                        <div>
                                            <p className="text-xs font-semibold text-gray-700">Tiempo estimado</p>
                                            <p className="text-xs text-gray-500">~{Math.ceil(currentCount * 6 / 60)} minutos para {currentCount} contactos</p>
                                        </div>
                                    </div>
                                );
                            })()}
                        </div>
                    )}

                    {/* STEP 2 */}
                    {step === 2 && (
                        <div style={{ animation: 'fadeSlideIn 0.3s ease-out' }}>
                            <div className="flex items-center justify-between mb-2">
                                <label className="text-sm font-semibold text-gray-700">Mensaje de WhatsApp *</label>
                                <span className={`text-xs font-medium ${messageTemplate.length > MAX_CHARS * 0.9 ? 'text-amber-500' : 'text-gray-400'}`}>{messageTemplate.length}/{MAX_CHARS}</span>
                            </div>

                            <div className="flex items-center gap-1.5 mb-2 flex-wrap">
                                <button onClick={() => setMessageTemplate(p => p + '{nombre}')} className="px-2.5 py-1 bg-purple-100 text-purple-700 rounded-lg text-xs font-bold hover:bg-purple-200 transition-colors">+ {'{nombre}'}</button>
                                {quickEmojis.map(e => (
                                    <button key={e} onClick={() => setMessageTemplate(p => p + e)} className="w-7 h-7 flex items-center justify-center hover:bg-gray-100 rounded-lg transition-colors text-base">{e}</button>
                                ))}
                            </div>

                            <textarea value={messageTemplate} onChange={e => setMessageTemplate(e.target.value)}
                                placeholder="✨ Querida {nombre}, tenemos algo especial para ti..."
                                rows={5} maxLength={MAX_CHARS} autoFocus
                                className="w-full px-4 py-3 bg-gray-50 rounded-xl text-sm border-2 border-transparent focus:border-purple-400 focus:bg-white outline-none transition-all resize-none" />
                            <p className="text-xs text-gray-400 mt-1">Usa <code className="bg-gray-100 px-1 rounded font-mono">{'{nombre}'}</code> para personalizar con el nombre de cada cliente.</p>

                            {messageTemplate && (
                                <div className="mt-4">
                                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Vista previa en WhatsApp</p>
                                    <div className="bg-[#e5ddd5] rounded-2xl p-4">
                                        <div className="flex items-end gap-2">
                                            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-teal-400 to-emerald-500 flex items-center justify-center text-white text-xs font-bold flex-shrink-0">S</div>
                                            <div className="bg-white rounded-2xl rounded-bl-sm px-4 py-3 max-w-xs shadow-sm">
                                                <p className="text-sm text-gray-800 whitespace-pre-wrap leading-relaxed">{previewMessage}</p>
                                                <div className="flex items-center justify-end gap-1 mt-1">
                                                    <span className="text-[10px] text-gray-400">{new Date().toLocaleTimeString('es-CO', { hour: '2-digit', minute: '2-digit' })}</span>
                                                    <span className="text-teal-500 text-xs">✓✓</span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    {/* STEP 3 */}
                    {step === 3 && (
                        <div style={{ animation: 'fadeSlideIn 0.3s ease-out' }}>
                            <div className="bg-gradient-to-br from-violet-50 to-purple-50 border border-violet-100 rounded-2xl p-4 space-y-3 mb-5">
                                <h3 className="text-sm font-bold text-violet-900">Resumen de Campaña</h3>
                                <div className="space-y-2 text-sm">
                                    <div className="flex justify-between"><span className="text-gray-500">Nombre</span><span className="font-semibold text-gray-800 truncate max-w-[200px]">{name}</span></div>
                                    <div className="flex justify-between"><span className="text-gray-500">Destinatarios</span><span className="font-semibold text-emerald-600">{selectedGroupId ? groups.find(g => g.id === selectedGroupId)?.memberCount : clientCount} contactos</span></div>
                                    <div className="flex justify-between"><span className="text-gray-500">Mensaje</span><span className="font-semibold text-gray-800">{messageTemplate.length} caracteres</span></div>
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-2">📅 Programar envío <span className="font-normal text-gray-400">(opcional)</span></label>
                                <input type="datetime-local" value={scheduledAt} onChange={e => setScheduledAt(e.target.value)}
                                    min={new Date().toISOString().slice(0, 16)}
                                    className="w-full px-4 py-3 bg-gray-50 rounded-xl text-sm border-2 border-transparent focus:border-purple-400 focus:bg-white outline-none transition-all" />
                                <p className="text-xs text-gray-400 mt-1.5">Si no seleccionas fecha, podrás enviarla manualmente después.</p>
                            </div>

                            {error && (
                                <div className="bg-red-50 border border-red-100 rounded-xl p-3 text-sm text-red-700 flex items-center gap-2">
                                    <span>⚠️</span> {error}
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="sticky bottom-0 bg-white border-t border-gray-100 px-6 py-4 flex gap-3 flex-shrink-0">
                    {step > 1 && (
                        <button onClick={() => setStep(s => s - 1)} disabled={loading}
                            className="px-4 py-3 rounded-xl border-2 border-gray-200 text-gray-600 text-sm font-semibold hover:bg-gray-50 transition-colors disabled:opacity-50 flex-shrink-0">
                            ← Atrás
                        </button>
                    )}
                    {step < 3 ? (
                        <button onClick={() => {
                            if (step === 1 && !name.trim()) { setError('El nombre es obligatorio.'); return; }
                            if (step === 2 && !messageTemplate.trim()) { setError('El mensaje es obligatorio.'); return; }
                            setError(null); setStep(s => s + 1);
                        }} className="flex-1 px-4 py-3 rounded-xl bg-gradient-to-r from-violet-600 to-purple-600 text-white text-sm font-semibold shadow-md hover:-translate-y-0.5 transition-all">
                            Continuar →
                        </button>
                    ) : (
                        <>
                            <button onClick={() => handleCreate(false)} disabled={loading}
                                className="flex-1 px-4 py-3 rounded-xl border-2 border-gray-200 text-gray-700 text-sm font-semibold hover:bg-gray-50 transition-colors disabled:opacity-50">
                                Guardar Borrador
                            </button>
                            <button onClick={() => handleCreate(true)} disabled={loading}
                                className="flex-1 px-4 py-3 rounded-xl bg-gradient-to-r from-violet-600 to-purple-600 text-white text-sm font-bold shadow-md hover:-translate-y-0.5 transition-all disabled:opacity-50">
                                {loading ? '⏳ Creando...' : '🚀 Programar Envío'}
                            </button>
                        </>
                    )}
                </div>
            </div>

            <style>{`
                @keyframes fadeIn { from { opacity: 0 } to { opacity: 1 } }
                @keyframes scaleIn { from { opacity: 0; transform: scale(0.92) translateY(20px) } to { opacity: 1; transform: scale(1) translateY(0) } }
                @keyframes fadeSlideIn { from { opacity: 0; transform: translateX(16px) } to { opacity: 1; transform: translateX(0) } }
            `}</style>
        </div>
    );
}

// ============================================
// Stat Card
// ============================================

function StatCard({ label, value, icon, color }: { label: string; value: string | number; icon: string; color: string }) {
    return (
        <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-50 hover:-translate-y-1 transition-all duration-300 hover:shadow-md">
            <div className="flex items-center justify-between">
                <div>
                    <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">{label}</p>
                    <p className={`text-2xl font-bold mt-1 ${color}`}>{value}</p>
                </div>
                <span className="text-3xl">{icon}</span>
            </div>
        </div>
    );
}

// ============================================
// Main Page
// ============================================

export default function CampaignsPage() {
    const salonId = useSalonId();
    const [campaigns, setCampaigns] = useState<Campaign[]>([]);
    const [clientCount, setClientCount] = useState(0);
    const [groups, setGroups] = useState<ClientGroup[]>([]);
    const [loading, setLoading] = useState(true);
    const [showDrawer, setShowDrawer] = useState(false);

    const loadData = useCallback(async () => {
        if (!salonId) return;
        setLoading(true);
        try {
            const [campaignsData, clientsData, groupsData] = await Promise.all([
                campaignApi.getCampaigns(salonId),
                clientApi.getSalonClients(salonId, false),
                clientGroupApi.getGroups(salonId),
            ]);
            setCampaigns(campaignsData);
            setClientCount(clientsData.length);
            setGroups(groupsData);
        } catch (e) {
            console.error('[CampaignsPage] Error loading data:', e);
        } finally {
            setLoading(false);
        }
    }, [salonId]);

    useEffect(() => {
        loadData();
    }, [loadData]);

    // Derived stats
    const totalSent = campaigns.reduce((sum, c) => sum + c.sentCount, 0);
    const totalRecipients = campaigns.reduce((sum, c) => sum + c.totalRecipients, 0);
    const successRate = totalRecipients > 0 ? ((totalSent / totalRecipients) * 100).toFixed(1) : '—';
    const active = campaigns.filter(c => c.status === 'IN_PROGRESS' || c.status === 'SCHEDULED').length;

    const formatDate = (date: string | null) => {
        if (!date) return '—';
        return new Date(date).toLocaleDateString('es-CO', { day: 'numeric', month: 'short', year: 'numeric' });
    };

    return (
        <div className="min-h-screen bg-gray-50 p-6">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
                <div>
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-2xl bg-gradient-to-br from-teal-600 to-teal-400 flex items-center justify-center text-white shadow-md">
                            <MegaphoneIcon />
                        </div>
                        <div>
                            <h1 className="text-2xl font-bold text-gray-900">Campañas de WhatsApp</h1>
                            <p className="text-sm text-gray-500">Envía mensajes masivos a tus clientes</p>
                        </div>
                    </div>
                </div>
                <button
                    id="btn-new-campaign"
                    onClick={() => setShowDrawer(true)}
                    className="inline-flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-violet-600 to-purple-600 text-white font-semibold rounded-xl shadow-md hover:shadow-violet-200 hover:-translate-y-0.5 transition-all text-sm"
                >
                    <PlusIcon />
                    Nueva Campaña
                </button>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
                <StatCard label="Total Campañas" value={campaigns.length} icon="📢" color="text-violet-600" />
                <StatCard label="Mensajes Enviados" value={totalSent.toLocaleString()} icon="✉️" color="text-teal-600" />
                <StatCard label="Tasa de Éxito" value={`${successRate}%`} icon="🎯" color="text-emerald-600" />
                <StatCard label="Activas Ahora" value={active} icon="🔄" color="text-blue-600" />
            </div>

            {/* Campaigns Table */}
            <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
                <div className="px-6 py-4 border-b border-gray-50">
                    <h2 className="text-base font-bold text-gray-800">Historial de Campañas</h2>
                </div>

                {loading ? (
                    <div className="p-12 text-center text-gray-400">
                        <div className="inline-block w-8 h-8 border-4 border-teal-200 border-t-teal-500 rounded-full animate-spin mb-3" />
                        <p>Cargando campañas...</p>
                    </div>
                ) : campaigns.length === 0 ? (
                    <div className="p-16 text-center">
                        <span className="text-6xl">📢</span>
                        <h3 className="text-lg font-semibold text-gray-700 mt-4">Aún no tienes campañas</h3>
                        <p className="text-sm text-gray-400 mt-1 mb-6">Crea tu primera campaña para el Día de la Madre, Semana Santa u otra fechas especiales.</p>
                        <button
                            onClick={() => setShowDrawer(true)}
                            className="inline-flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-teal-600 to-teal-500 text-white font-semibold rounded-xl text-sm"
                        >
                            <PlusIcon />
                            Crear Primera Campaña
                        </button>
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full">
                            <thead>
                                <tr className="text-xs font-semibold text-gray-500 uppercase tracking-wider bg-gray-50/80">
                                    <th className="px-6 py-3.5 text-left">Campaña</th>
                                    <th className="px-4 py-3.5 text-center">Destinatarios</th>
                                    <th className="px-4 py-3.5 text-center">Enviados</th>
                                    <th className="px-4 py-3.5 text-center">Fallidos</th>
                                    <th className="px-4 py-3.5 text-left">Estado</th>
                                    <th className="px-6 py-3.5 text-right">Fecha</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-50">
                                {campaigns.map(campaign => (
                                    <tr
                                        key={campaign.id}
                                        className="hover:bg-gray-50/80 transition-colors duration-150"
                                    >
                                        <td className="px-6 py-4">
                                            <p className="font-semibold text-sm text-gray-900">{campaign.name}</p>
                                            <p className="text-xs text-gray-400 mt-0.5 truncate max-w-xs">
                                                {campaign.messageTemplate.substring(0, 60)}...
                                            </p>
                                        </td>
                                        <td className="px-4 py-4 text-center">
                                            <span className="text-sm font-medium text-gray-700">{campaign.totalRecipients}</span>
                                        </td>
                                        <td className="px-4 py-4 text-center">
                                            <span className="text-sm font-semibold text-emerald-600">{campaign.sentCount}</span>
                                        </td>
                                        <td className="px-4 py-4 text-center">
                                            <span className={`text-sm font-semibold ${campaign.failedCount > 0 ? 'text-red-500' : 'text-gray-400'}`}>
                                                {campaign.failedCount}
                                            </span>
                                        </td>
                                        <td className="px-4 py-4">
                                            <StatusBadge
                                                status={campaign.status}
                                                sentCount={campaign.sentCount}
                                                totalRecipients={campaign.totalRecipients}
                                            />
                                        </td>
                                        <td className="px-6 py-4 text-right text-xs text-gray-400">
                                            {formatDate(campaign.scheduledAt || campaign.createdAt)}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* Create Drawer */}
            {salonId && (
                <CreateCampaignDrawer
                    isOpen={showDrawer}
                    onClose={() => setShowDrawer(false)}
                    onCreated={loadData}
                    salonId={salonId}
                    clientCount={clientCount}
                    groups={groups}
                />
            )}
        </div>
    );
}
